<template>
  <div>
    <template v-if="isLoaded">
      <BaseTable
        :items="audits"
        :headers="tableHeaders"
        :loading="isLoading"
        :pagination="pagination"
        :idPrefix="idPrefix"
        :skeletonCount="10"
      >
        <!-- Custom skeleton slot -->
        <template #skeleton>
          <td class="p-4">
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </td>
          <td class="p-4">
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </td>
          <td class="p-4">
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </td>
          <td class="p-4">
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </td>
          <td class="p-4">
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </td>
        </template>

        <!-- Custom item slot -->
        <template #item="{ item }">
          <td class="p-4 text-sm text-gray-900">
            <div>
              {{ item?.attributes?.["created-at"] | formatDate }}
            </div>
          </td>
          <td class="p-4 text-sm text-gray-900">
            <div>
              {{ item?.attributes?.user?.username }}
            </div>
          </td>
          <td class="p-4 text-sm text-gray-900">
            <div>
              {{ item?.attributes?.action }}
            </div>
          </td>
          <td class="p-4 text-sm">
            <div
              v-html="convertPayload(item?.attributes?.['old-values'])"
            ></div>
          </td>
          <td class="p-4 text-sm">
            <div
              v-html="convertPayload(item?.attributes?.['new-values'])"
            ></div>
          </td>
        </template>

        <!-- Empty state slot -->
        <template #empty> No Audit found </template>
      </BaseTable>
      <div class="show custom-pagination d-flex justify-content-center my-5">
        <base-cursor-pagination
          :meta="pagination"
          @pageChange="onAuditPageChange"
        />
      </div>
    </template>
    <BaseLoader v-if="!isLoaded" size="xl" />
  </div>
</template>

<script>
import { translations } from "@/mixins";
import BaseTable from "@/components/BaseTable.vue";
import BaseCursorPagination from "@/components/BaseCursorPagination";
import { AuditLog as AuditLogApi } from "@/services/SOLO";
import moment from "moment";
import BaseLoader from "../BaseLoader.vue";

export default {
  name: "AuditList",

  components: {
    BaseTable,
    BaseCursorPagination,
    BaseLoader,
  },

  filters: {
    formatDate(date) {
      return moment
        .utc(date, "YYYY-MM-D hh:mm:ss")
        .locale("en-us")
        .local()
        .format("D/MM/YYYY hh:mm:ss");
    },
  },

  mixins: [translations],

  props: {
    auditType: {
      type: String,
      required: true,
    },
    auditId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoaded: false,
      isLoading: true,
      idPrefix: "audit.",
      tableHeaders: [],
      audits: [],
      pagination: null,
    };
  },

  mounted() {
    this.getAudits(this.auditId);
  },

  created() {
    // Set table headers after component is created and mixins are applied
    this.tableHeaders = [
      {
        label: this.translate("date/time"),
        id: "headerdatetime",
      },
      {
        label: this.translate("username"),
        id: "headerusername",
      },
      {
        label: this.translate("action"),
        id: "headeraction",
      },
      {
        label: this.translate("old data"),
        id: "headerolddata",
      },
      {
        label: this.translate("new data"),
        id: "headernewdata",
      },
    ];
  },

  methods: {
    convertPayload(data) {
      let html = "";

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'updated_at') {
          value = moment
            .utc(value, "YYYY-MM-D hh:mm:ss")
            .locale("en-us")
            .local()
            .format("D/MM/YYYY hh:mm:ss")
        }
        
        try {
          // Attempt to parse stringified JSON objects
          if (typeof value === "string") {
            const parsed = JSON.parse(value);
            // Only assign parsed if it results in an object/array
            if (typeof parsed === "object") {
              value = parsed;
            }
          }
        } catch {
          // Leave value as is if JSON.parse fails
        }

        // Handle null or undefined
        if (value === null || value === undefined) {
          value = "-";
        }

        // Handle non-object values (string, number, boolean)
        if (typeof value !== "object") {
          html += `
          <div>
            <strong>${key}</strong>:<br> ${value}
          </div>
        `;
        }
        // Handle arrays
        else if (Array.isArray(value)) {
          // Check if array contains objects
          const isObjectArray = value.some((item) => typeof item === "object");
          if (isObjectArray) {
            html += `
          <div>
            <strong>${key}</strong>:<br>
            <div class="ml-3">
              ${value
                .map(
                  (item, index) => `
                <div><strong>[${index}]</strong> ${this.convertPayload(
                    item
                  )}</div>
              `
                )
                .join("")}
            </div>
          </div>
        `;
          } else {
            html += `
          <div>
            <strong>${key}</strong>:<br> ${value.join(", ")}
          </div>
        `;
          }
        }
        // Handle nested object
        else {
          html += `
        <div>
          <strong>${key}</strong>:
          <div class="ml-3">
            ${this.convertPayload(value)}
          </div>
        </div>
      `;
        }
      });

      return html;
    },
    async getAudits(id, cursor = null) {
      this.isLoading = true;
      try {
        const response = await AuditLogApi.all(
          this.auditType,
          id,
          1,
          10,
          cursor
        );
        const {
          data,
          meta: { pagination },
        } = response?.data;
        this.audits = data;
        this.pagination = {
          ...pagination,
          enabled: false,
        };
      } catch (e) {
        console.log("Error Getting Audits", e);
      } finally {
        this.isLoading = false;
        this.isLoaded = true;
      }
    },
    onAuditPageChange(cursor) {
      this.getAudits(this.auditId, cursor);
    },
  },
};
</script>

<style scoped>
</style>